/**
 * Version comparison utilities.
 *
 * Supports both standard semver (0.0.7) and dev versions (0.0.7-dev.BRANCH.sha).
 */

/**
 * Default fallback for invalid/missing version strings.
 */
const DEFAULT_VERSION = { major: 0, minor: 0, patch: 0, prerelease: null };

/**
 * Validate that a string looks like a version number.
 * Accepts: "0.0.7", "1.2.3-dev.branch.sha", "0.0.0"
 */
export function isValidVersionString(version: unknown): version is string {
  if (typeof version !== "string" || !version.trim()) {
    return false;
  }
  // Basic pattern: digits.digits.digits with optional -prerelease
  // More permissive than strict semver to handle edge cases
  return /^\d+\.\d+\.\d+(-[\w.-]+)?$/.test(version.trim());
}

/**
 * Parse a version string into its components.
 * Returns { major, minor, patch, prerelease } where prerelease is the dev suffix if present.
 *
 * Handles invalid input gracefully by returning 0.0.0.
 */
export function parseVersion(version: string): {
  major: number;
  minor: number;
  patch: number;
  prerelease: string | null;
} {
  // Guard against invalid input
  if (!isValidVersionString(version)) {
    console.warn("[versionUtils] Invalid version string:", version);
    return { ...DEFAULT_VERSION };
  }

  const trimmed = version.trim();

  // Handle dev versions: "0.0.7-dev.BRANCH.sha"
  const [baseVersion, ...prereleaseParts] = trimmed.split("-");
  const prerelease =
    prereleaseParts.length > 0 ? prereleaseParts.join("-") : null;

  // Parse base version
  const parts = baseVersion.split(".");
  const major = parseInt(parts[0] || "0", 10);
  const minor = parseInt(parts[1] || "0", 10);
  const patch = parseInt(parts[2] || "0", 10);

  // Final NaN guard (shouldn't happen with regex validation, but defense-in-depth)
  if (Number.isNaN(major) || Number.isNaN(minor) || Number.isNaN(patch)) {
    console.warn("[versionUtils] Version parsing resulted in NaN:", version);
    return { ...DEFAULT_VERSION };
  }

  return { major, minor, patch, prerelease };
}

/**
 * Compare two version strings.
 * Returns:
 *   -1 if a < b
 *    0 if a == b
 *    1 if a > b
 *
 * Note: Dev versions are considered LESS than their base version.
 * e.g., "0.0.7-dev.X" < "0.0.7"
 */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const vA = parseVersion(a);
  const vB = parseVersion(b);

  // Compare major.minor.patch
  if (vA.major !== vB.major) return vA.major < vB.major ? -1 : 1;
  if (vA.minor !== vB.minor) return vA.minor < vB.minor ? -1 : 1;
  if (vA.patch !== vB.patch) return vA.patch < vB.patch ? -1 : 1;

  // If base versions are equal, compare prerelease
  // No prerelease > prerelease (0.0.7 > 0.0.7-dev.X)
  if (vA.prerelease === null && vB.prerelease !== null) return 1;
  if (vA.prerelease !== null && vB.prerelease === null) return -1;

  // Both have prerelease - compare lexicographically
  if (vA.prerelease !== null && vB.prerelease !== null) {
    if (vA.prerelease < vB.prerelease) return -1;
    if (vA.prerelease > vB.prerelease) return 1;
  }

  return 0;
}

/**
 * Check if version `a` is less than version `b`.
 */
export function isVersionLessThan(a: string, b: string): boolean {
  return compareVersions(a, b) === -1;
}

/**
 * Check if version `a` is greater than version `b`.
 */
export function isVersionGreaterThan(a: string, b: string): boolean {
  return compareVersions(a, b) === 1;
}

/**
 * Check if version `a` equals version `b`.
 */
export function isVersionEqual(a: string, b: string): boolean {
  return compareVersions(a, b) === 0;
}

/**
 * Check if version `a` is greater than or equal to version `b`.
 */
export function isVersionGreaterThanOrEqual(a: string, b: string): boolean {
  return compareVersions(a, b) >= 0;
}
